import { inject, Injectable } from '@angular/core';
import { groupBy, sortBy } from 'lodash';
import { TreeNode } from 'primeng/api';
import { LanguageService } from '../../core/services/language.service';
import { EquipmentTypesGroupedSelectOption } from '../filters/contract/equipment-types-grouped-select-option.contract';
import { EquipmentSubTypeFilterViewMerged } from '../filters/contract/filter-view/equipment-sub-type-filter-view-merged.contract';
import { EquipmentSubTypeFilterView } from '../filters/contract/filter-view/equipment-subtype-filter-view.contract';
import { EquipmentTypeFilterViewMerged } from '../filters/contract/filter-view/equipment-type-filter-view-merged.contract';
import { EquipmentTypeFilterView } from '../filters/contract/filter-view/equipment-type-filter-view.contract';
import { SEPARATOR } from '../filters/filter.const';

/**
 * Service responsible for merging duplicated equipment sub-types into a single logical option
 * based on their translated Category 2 name. Provides helpers to:
 * - build grouped select options and tree select options for UI components
 * - map between original equipment type ids and merged ids
 * - sort option groups and children consistently
 */
@Injectable({ providedIn: 'root' })
export class EquipmentTypeMergeService {
  private languageService = inject(LanguageService);

  toGroupedSelectOptions(types: EquipmentTypeFilterView[]): EquipmentTypesGroupedSelectOption[] {
    const options = this.merge(types)
      .map((type) => {
        const children = type.subTypes.map((subType) => ({
          value: subType.equipmentTypeMergedId,
          label: subType.category2NameTranslated,
          data: subType,
          expanded: true,
        }));

        return {
          category1Name: this.languageService.resolveI18NObject(type.category1Name),
          items: sortBy(children, ['label']),
        };
      });

    return sortBy(options, ['category1Name']);
  }

  toGroupedTreeSelectOptions(types: EquipmentTypeFilterView[]): TreeNode<EquipmentSubTypeFilterViewMerged>[] {
    const options = this.merge(types)
      .map((type) => {
        const children = type.subTypes.map((subType) => ({
          key: subType.equipmentTypeMergedId,
          data: subType,
          label: subType.category2NameTranslated,
          expanded: true,
        }));
        return {
          key: type.category1,
          label: this.languageService.resolveI18NObject(type.category1Name),
          expanded: true,
          children: sortBy(children, ['label']),
        };
      });

    return sortBy(options, ['label']);
  }

  findOptionIdsByOriginal(options: EquipmentTypesGroupedSelectOption[], selectedIds: string[]): string[];
  findOptionIdsByOriginal(options: TreeNode<EquipmentSubTypeFilterViewMerged>[], selectedIds: string[]): string[];
  findOptionIdsByOriginal(
    options: EquipmentTypesGroupedSelectOption[] | TreeNode<EquipmentSubTypeFilterViewMerged>[],
    selectedIds: string[],
  ): string[] {
    if (!options || options.length === 0) {
      return [];
    }

    const first: EquipmentTypesGroupedSelectOption | TreeNode<EquipmentSubTypeFilterViewMerged> = options[0] as
      | EquipmentTypesGroupedSelectOption
      | TreeNode<EquipmentSubTypeFilterViewMerged>;

    // Detect grouped select option shape by presence of 'items'
    if ('items' in first) {
      const typed = options as EquipmentTypesGroupedSelectOption[];
      return this.findKeysByOriginalIds(
        typed,
        selectedIds,
        (option) => option.items,
        (child) => child.value,
        (child) => child.data?.equipmentTypeIds,
      );
    }

    // Otherwise treat as TreeNode shape
    const typed = options as TreeNode<EquipmentSubTypeFilterViewMerged>[];
    return this.findKeysByOriginalIds(
      typed,
      selectedIds,
      (option) => option.children,
      (child) => child.key,
      (child) => child.data?.equipmentTypeIds,
    );
  }

  getOriginalIds(selectedMergedIds: string[]): string[] {
    return selectedMergedIds.map((merged) => merged.split(SEPARATOR)).flat();
  }

  private findKeysByOriginalIds<TOption, TChild>(
    options: TOption[],
    selectedIds: string[],
    getChildren: (option: TOption) => TChild[] | undefined,
    getKey: (child: TChild) => string | undefined,
    getEquipmentTypeIds: (child: TChild) => string[] | undefined,
  ): string[] {
    const idsSet = new Set(selectedIds);
    const result: string[] = [];
    options.forEach((option) => {
      const children = getChildren(option) ?? [];
      children.forEach((child) => {
        const key = getKey(child);
        const equipmentTypeIds = getEquipmentTypeIds(child) ?? [];
        if (key && equipmentTypeIds.some((typeId) => idsSet.has(typeId))) {
          result.push(key);
        }
      });
    });
    return result;
  }

  private merge(types: EquipmentTypeFilterView[]): EquipmentTypeFilterViewMerged[] {
    return types.map((type) => ({
      ...type,
      subTypes: this.mergeEquipmentSubTypesByCriteria(type.subTypes),
    }));
  }

  private mergeEquipmentSubTypesByCriteria(subTypes: EquipmentSubTypeFilterView[]): EquipmentSubTypeFilterViewMerged[] {
    // Merging criteria is translated "category2"
    const translated = subTypes.map((item) => ({
      ...item,
      category2NameTranslated: this.languageService.resolveI18NObject(item.category2Name) ?? item.category2
    }));

    const grouped = groupBy(translated, 'category2NameTranslated');
    return Object.keys(grouped).map((category2NameTranslated) => {
      const mergingTypes = grouped[category2NameTranslated];
      const equipmentTypeIds = mergingTypes.map(({ equipmentTypeId }) => equipmentTypeId);
      return {
        equipmentTypeIds,
        equipmentTypeMergedId: equipmentTypeIds.join(SEPARATOR),
        category2NameTranslated,
        count: mergingTypes.reduce((result, { count }) => result + (count ?? 0), 0),
      }
    });
  }


}
